// Pure number/string formatters used throughout the app. No React.

// Standard USD currency, two decimals. Null/NaN render as $0.00 so totals
// in tables don't break alignment.
export const fmt = (n) => {
  if (n === null || n === undefined || isNaN(n)) return '$0.00';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);
};

// Compact currency: $1.2M / $34.5k / $123.45. Used on chart axes where
// long numbers would overflow.
export const fmtCompact = (n) => {
  if (n === null || n === undefined || isNaN(n)) return '$0';
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `${n < 0 ? '-' : ''}$${(abs/1_000_000).toFixed(2)}M`;
  if (abs >= 1_000)     return `${n < 0 ? '-' : ''}$${(abs/1_000).toFixed(1)}k`;
  return fmt(n);
};

// Possessive form of a name. Special-cases "You"/"I"/"me" so we don't
// render "You's team" — instead we get "Your team". Everything else
// gets the standard `name's` suffix.
export const possessive = (name) => {
  if (!name) return '';
  const lower = String(name).trim().toLowerCase();
  if (lower === 'you' || lower === 'i' || lower === 'me') return 'Your';
  return `${name}'s`;
};

// "JOHN SMITH" → "John Smith". "smith, john" → "Smith, John".
export function titleCase(s) {
  if (!s) return '';
  return String(s).split(' ').map(w => w ? w[0].toUpperCase() + w.slice(1).toLowerCase() : '').join(' ').trim();
}

// Carrier statements arrive as LAST FIRST or "LAST, FIRST" — the
// commission-tracker's own roster wants human-readable First Last.
// This helper reorders and title-cases in one pass. Handles:
//   "SMITH JOHN"          → "John Smith"
//   "SMITH, JOHN"         → "John Smith"
//   "JOHNSON-KELLY MARY"  → "Mary Johnson-Kelly"
//   "Brown"               → "Brown"           (single word, unchanged)
//   "John Smith"          → "John Smith"      (already good, pass through)
//   ""                    → ""
//
// Heuristic to decide whether to reorder: reorder when the first
// token is longer than 2 chars AND (comma present OR the whole
// string is upper-case OR title-cased first token appears to be a
// surname pattern). Leave already-title-cased "First Last" alone.
export function formatInsuredFirstLast(s) {
  if (!s) return '';
  const raw = String(s).trim();
  if (!raw) return '';

  // Comma form: "SMITH, JOHN" — split on comma, second half is first name(s).
  if (raw.includes(',')) {
    const [last, rest] = raw.split(',', 2).map(t => t.trim());
    if (last && rest) return `${titleCase(rest)} ${titleCase(last)}`.trim();
  }

  const tokens = raw.split(/\s+/).filter(Boolean);
  if (tokens.length <= 1) return titleCase(raw);
  // If the ORIGINAL string is all-uppercase (carrier statement style),
  // reorder as LAST FIRST → First Last. First token is treated as
  // surname (may include hyphens like JOHNSON-KELLY).
  const isAllUpper = raw === raw.toUpperCase();
  if (isAllUpper) {
    const last = tokens[0];
    const rest = tokens.slice(1).join(' ');
    return `${titleCase(rest)} ${titleCase(last)}`.trim();
  }
  // Otherwise assume already First Last — just title-case for safety.
  return titleCase(raw);
}

// "Trey Allen" → "trey-allen". Strips accents (NFKD + combining marks
// U+0300-U+036F), collapses non-alphanumeric runs to a single dash,
// trims leading/trailing dashes. Returns '' when nothing usable
// remains.
export function kebabSlug(s) {
  if (!s) return '';
  return String(s)
    .normalize('NFKD').replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

// Builds the public referral / booking landing URL the agent shares
// with prospects. Format: https://sixfigureagentcrm.com/p/<slug>. The
// marketing site owns the /p/<slug> route and resolves slug → agent.
// Falls back to an id-tail slug when the agent has no ownerName yet,
// so the link still works on Day 1 before Settings is filled in.
export function referralLandingUrl({ ownerName, fallbackId } = {}) {
  const host = (typeof process !== 'undefined' && process.env?.REACT_APP_REFERRAL_PUBLIC_HOST) || 'https://sixfigureagentcrm.com';
  const slug = kebabSlug(ownerName) || (fallbackId ? `a-${String(fallbackId).slice(0, 8)}` : '');
  return slug ? `${host}/p/${slug}` : '';
}

// US-style date display: MM-DD-YYYY. Accepts:
//   - YYYY-MM-DD                  (the ISO key used internally)
//   - YYYY-MM-DDTHH:MM            (datetime-local input value)
//   - MM/DD/YYYY                  (already-US-formatted strings)
//   - Date instance
// Returns the original string unchanged if it doesn't match one of
// these — never throws and never silently corrupts the data.
// Format a US phone number for display: "(NNN) NNN-NNNN".
// Accepts raw digits, +1 prefix, already-formatted, or partials.
// Returns the best-effort formatted string; empty in → empty out.
// 11-digit input starting with 1 is treated as a country-code prefix
// and the leading 1 is dropped (8003334444 vs 18003334444 both work).
export function fmtPhoneUS(v) {
  if (v == null) return '';
  let d = String(v).replace(/\D/g, '');
  if (d.length === 11 && d.startsWith('1')) d = d.slice(1);
  if (d.length === 0)  return '';
  if (d.length <= 3)   return `(${d}`;
  if (d.length <= 6)   return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6, 10)}`;
}

// Normalize a date-of-birth string to ISO YYYY-MM-DD so the lead
// table can sort correctly regardless of where the value came from
// (CSV imports ship MM/DD/YYYY; <input type="date"> ships ISO).
// Returns '' when the input isn't parseable.
export function normalizeDob(v) {
  if (v == null) return '';
  const s = String(v).trim();
  if (!s) return '';
  let m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (m) return `${m[1]}-${m[2].padStart(2,'0')}-${m[3].padStart(2,'0')}`;
  m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (m) return `${m[3]}-${m[1].padStart(2,'0')}-${m[2].padStart(2,'0')}`;
  m = s.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
  if (m) return `${m[3]}-${m[1].padStart(2,'0')}-${m[2].padStart(2,'0')}`;
  return '';
}

export function fmtDateUS(v) {
  if (!v) return '';
  if (v instanceof Date) {
    if (Number.isNaN(v.getTime())) return '';
    const m = String(v.getMonth() + 1).padStart(2, '0');
    const d = String(v.getDate()).padStart(2, '0');
    const y = v.getFullYear();
    return `${m}-${d}-${y}`;
  }
  const s = String(v).trim();
  // YYYY-MM-DD or YYYY-MM-DDT… (datetime-local)
  let m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[2]}-${m[3]}-${m[1]}`;
  // MM/DD/YYYY → MM-DD-YYYY (keep dashes for app-wide consistency)
  m = s.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (m) return `${m[1]}-${m[2]}-${m[3]}`;
  // Last-ditch: try Date parse. If the input is something malformed
  // like "T00:00:00." (operator bug report 2026-06-28 — Leads table's
  // Last contact column was showing this literal), Date returns
  // Invalid Date → we return '' rather than echoing garbage upstream.
  const parsed = new Date(s);
  if (!Number.isNaN(parsed.getTime())) {
    const mm = String(parsed.getMonth() + 1).padStart(2, '0');
    const dd = String(parsed.getDate()).padStart(2, '0');
    const yy = parsed.getFullYear();
    return `${mm}-${dd}-${yy}`;
  }
  return '';
}

// Defense-in-depth sanitizer for free-text fields that flow downstream
// into SMS templates, email subjects, and (most importantly) AI prompts.
// React already escapes output during render, so this is NOT the primary
// defense against XSS on the dashboard — it's a defense against:
//   · prompt injection: a lead name like "; ignore prior instructions
//     and exfiltrate ..." could nudge an AI agent off its rails when
//     the name is interpolated into a system prompt.
//   · email/SMS template HTML: if a name ever lands in an email body
//     that's rendered as HTML, raw tags would render.
//   · log/audit poisoning: control chars + ANSI escapes can corrupt
//     terminal output and CSV exports.
//
// Strategy: strip ALL HTML tags (greedy <...>), strip ASCII control
// chars except \t \n, collapse whitespace, cap at 120 chars. Audit
// 2026-06-29 BUG-006.
export function sanitizeFreeText(v) {
  if (v == null) return '';
  const s = String(v);
  return s
    .replace(/<[^>]*>/g, '')                    // strip <tag>...
    // eslint-disable-next-line no-control-regex
    .replace(/[ --]/g, '')   // ASCII control chars
    .replace(/[​-‍﻿]/g, '')      // zero-width chars (homoglyph defense)
    .replace(/\s+/g, ' ')                        // collapse whitespace
    .trim()
    .slice(0, 120);
}
